import { useState } from 'react'
import toast from 'react-hot-toast'
import { Download, Trash2 } from 'lucide-react'
import { DEFAULT_BIZ_LOGIC } from '@/api/projectDb'

/* ── Database & Runtime selectors ────────────────────── */
//
// Two small radio-card pickers that drive `application.db_mode` and
// `application.biz_logic` on the project, plus the runtime-bundle download and
// workspace clean-up buttons. Moved here from PrototypeTab with App settings;
// behaviour unchanged.

interface RadioCardProps<V extends string> {
  value: V
  current: V
  onSelect: (v: V) => void
  disabled?: boolean
  title: string
  blurb: string
  badge?: string
}

export function RadioCard<V extends string>({
  value, current, onSelect, disabled, title, blurb, badge,
}: RadioCardProps<V>) {
  const selected = value === current
  return (
    <button
      type="button"
      onClick={() => !disabled && onSelect(value)}
      disabled={disabled}
      style={{
        flex: 1,
        textAlign: 'left',
        padding: '0.625rem 0.75rem',
        background: selected ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
        border: `1px solid ${selected ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        boxShadow: selected ? '0 0 0 1px var(--color-accent) inset' : 'none',
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.8875rem', fontWeight: 600 }}>{title}</span>
        {badge && (
          <span
            style={{
              fontSize: '0.8125rem',
              padding: '1px 6px',
              borderRadius: 999,
              background: 'var(--color-bg-secondary)',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)',
            }}
          >
            {badge}
          </span>
        )}
      </div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
        {blurb}
      </div>
    </button>
  )
}

export function DbModeSelector({
  value, onChange, disabled,
}: {
  value: 'postgres' | 'sqlite' | 'oracle'
  onChange: (mode: 'postgres' | 'sqlite' | 'oracle') => void
  disabled?: boolean
}) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 6 }}>
        Database
      </label>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <RadioCard
          value="postgres"
          current={value}
          onSelect={onChange}
          disabled={disabled}
          title="Postgres"
          blurb="Tenant preview DB; full feature surface incl. PL/pgSQL."
          badge="default"
        />
        <RadioCard
          value="sqlite"
          current={value}
          onSelect={onChange}
          disabled={disabled}
          title="SQLite"
          blurb="Bundled file; runs in the standalone downloadable runtime."
        />
        <RadioCard
          value="oracle"
          current={value}
          onSelect={onChange}
          disabled={disabled}
          title="Oracle"
          blurb="Bring-your-own Oracle (19c+) via a project database connection."
        />
      </div>
    </div>
  )
}

export function BizLogicSelector({
  value, dbMode, onChange,
}: {
  value: 'plpgsql' | 'plsql' | 'python' | 'java'
  dbMode: 'postgres' | 'sqlite' | 'oracle'
  onChange: (lang: 'plpgsql' | 'plsql' | 'python' | 'java') => void
}) {
  const lockedToPython = dbMode === 'sqlite'
  const isOracle = dbMode === 'oracle'
  // Validity matrix: SQLite → Python only. Oracle → PL/SQL, Python or Java.
  // Postgres → PL/pgSQL, Python or Java.
  //
  // Oracle + Java is the one combo that depends on WHERE the app runs: the
  // Python runtime's Java sidecar is Postgres-only, while the J2EE (WildFly)
  // bl-host executes a bean against the engine's own connection whatever the
  // dialect. So it is offered here and labelled, rather than disabled — an
  // imported Proteus/iBase app on Oracle has Java beans and needs it.
  const needsJ2ee = isOracle && value === 'java'
  const current = lockedToPython
    ? 'python'
    : isOracle && value === 'plpgsql'
      ? 'plsql'
      : !isOracle && value === 'plsql'
        ? DEFAULT_BIZ_LOGIC.postgres
        : value
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 6 }}>
        Business logic
      </label>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {isOracle ? (
          <RadioCard
            value="plsql"
            current={current}
            onSelect={onChange}
            title="PL/SQL"
            blurb="Oracle stored functions (.sql files). Runs in-database."
            badge="oracle only"
          />
        ) : (
          <RadioCard
            value="plpgsql"
            current={current}
            onSelect={onChange}
            disabled={lockedToPython}
            title="PL/pgSQL"
            blurb="Postgres stored procedures (.sql files). Runs in-database."
            badge="postgres only"
          />
        )}
        <RadioCard
          value="python"
          current={current}
          onSelect={onChange}
          title="Python"
          blurb="Sandboxed .py files. Works on every database."
          badge={lockedToPython ? 'required' : undefined}
        />
        <RadioCard
          value="java"
          current={current}
          onSelect={onChange}
          disabled={lockedToPython}
          title="Java"
          blurb={isOracle
            ? 'Proteus/iBase-style .java files run by the J2EE runtime\u2019s bl-host.'
            : 'Proteus/iBase-style .java files run by the Java sidecar.'}
          badge={isOracle ? 'J2EE runtime' : 'postgres only'}
        />
      </div>
      {lockedToPython && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
          SQLite doesn't support PL/pgSQL or Java — business logic must be authored in Python.
        </p>
      )}
      {isOracle && !needsJ2ee && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
          Oracle projects author business logic in Oracle PL/SQL, Python, or Java
          (Java runs on the J2EE runtime only).
        </p>
      )}
      {needsJ2ee && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-warning)', marginTop: 4 }}>
          Oracle + Java runs on the <strong>J2EE (WildFly) runtime</strong> only —
          Advanced → Deploy to J2EE runtime. Live preview and the downloadable
          Python runtime use a Postgres-only Java sidecar, so these functions
          resolve to &ldquo;no rule&rdquo; there.
        </p>
      )}
    </div>
  )
}

export function DownloadRuntimeButton({
  folderName, disabled,
}: {
  folderName: string
  disabled?: boolean
}) {
  const [busy, setBusy] = useState(false)

  const handleClick = async () => {
    setBusy(true)
    try {
      const { downloadRuntimeBundle } = await import('@/api/workbench')
      const summary = await downloadRuntimeBundle(folderName)
      toast.success(
        `Downloaded ${summary.filename} (${summary.dbMode}/${summary.bizLogic}, ${summary.fileCount} files).`,
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Download failed.'
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      borderTop: '1px dashed var(--color-border)',
      paddingTop: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap',
    }}>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleClick}
        disabled={disabled || busy}
        title={disabled ? 'Save the project first' : 'Download a zip you can drop into the standalone Twasta runtime'}
      >
        <Download size={16} />
        {busy ? 'Preparing…' : 'Download runtime bundle'}
      </button>
      <p style={{
        margin: 0, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flex: 1, minWidth: 220,
      }}>
        Drop the zip into <code>runtime/projects/</code> on a downloaded runtime server.
        See <a href="https://github.com/anthropics" target="_blank" rel="noreferrer">the runtime README</a> for setup.
      </p>
    </div>
  )
}

export function CleanupWorkspaceButton({
  folderName, disabled,
}: {
  folderName: string
  disabled?: boolean
}) {
  const [busy, setBusy] = useState(false)

  const handleClick = async () => {
    setBusy(true)
    try {
      const { listWorkspaceJunk, cleanWorkspaceJunk } = await import('@/api/workbench')
      const preview = await listWorkspaceJunk(folderName)
      if (!preview.count) {
        toast.success('Workspace is already clean — no stray files found.')
        return
      }
      const sample = preview.removed.slice(0, 8).map((r) => `  • ${r}`).join('\n')
      const more = preview.count > 8 ? `\n  …and ${preview.count - 8} more` : ''
      const ok = window.confirm(
        `Remove ${preview.count} stray/junk file(s)?\n\n${sample}${more}\n\n` +
        'These are temp files, rename/seed backups, error dumps and agent scratch. ' +
        'Design files and recoverable model backups are never touched.',
      )
      if (!ok) return
      const res = await cleanWorkspaceJunk(folderName)
      toast.success(`Removed ${res.count} junk file(s) (${Math.round((res.freed_bytes || 0) / 1024)} KB freed).`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Cleanup failed.'
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      borderTop: '1px dashed var(--color-border)',
      paddingTop: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap',
    }}>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleClick}
        disabled={disabled || busy}
        title={disabled ? 'Save the project first' : 'Remove stray temp files, backups, error dumps and agent scratch'}
      >
        <Trash2 size={16} />
        {busy ? 'Cleaning…' : 'Clean up workspace'}
      </button>
      <p style={{
        margin: 0, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flex: 1, minWidth: 220,
      }}>
        Removes stray temp files, rename/seed backups, error dumps and agent scratch.
        Your design files and recoverable model backups are never touched.
      </p>
    </div>
  )
}
